import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTreeModule } from '@angular/material/tree';

interface TreeNode {
  name: string;
  children?: TreeNode[];
}

interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
}

@Component({
    selector: 'formly-autocomplete-tree',
    template: `
    <mat-form-field appearance="fill">
      <input
        matInput
        [placeholder]="to.placeholder!"
        [formControl]="control"
        [matAutocomplete]="auto"
        (input)="onInput($event)"  
      />
    </mat-form-field>

    <mat-autocomplete #auto="matAutocomplete">
      <mat-tree [dataSource]="dataSource" [treeControl]="treeControl">
        <mat-tree-node *matTreeNodeDef="let node" matAutocompleteOption>
          <li mat-tree-node>{{ node.name }}</li>
        </mat-tree-node>

        <mat-tree-node *matTreeNodeDef="let node; when: hasChild" matAutocompleteOption>
          <li>
            <div class="mat-tree-node">
              <button
                mat-icon-button
                matTreeNodeToggle
                [attr.aria-label]="'toggle ' + node.name"
              >
                <mat-icon>
                  {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                </mat-icon>
              </button>
              {{ node.name }}
            </div>
          </li>
        </mat-tree-node>
      </mat-tree>
    </mat-autocomplete>
  `,
    imports: [
        ReactiveFormsModule,
        MatTreeModule,
        MatIconModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
    ]
})
export class AutocompleteTreeTypeComponent extends FieldType<FormlyFieldConfig> implements OnInit {
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    (node: TreeNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
    }),
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  filteredTreeData: TreeNode[] = [];
  originalTreeData: TreeNode[] = [
    {
      name: 'Fruit',
      children: [
        { name: 'Apple' },
        { name: 'Orange' },
        { name: 'Banana' },
      ],
    },
    {
      name: 'Vegetables',
      children: [
        {
          name: 'Green',
          children: [{ name: 'Broccoli' }, { name: 'Brussels sprouts' }],
        },
        {
          name: 'Orange',
          children: [{ name: 'Pumpkin' }, { name: 'Carrot' }],
        },
      ],
    },
  ];

  // Getter to cast formControl to FormControl type
  get control(): FormControl {
    return this.formControl as FormControl;
  }

  ngOnInit() {
    this.filteredTreeData = this.originalTreeData;
    this.dataSource.data = this.filteredTreeData;
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  // Separate handler for input event
  onInput(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.filterTree(inputValue);
  }

  filterTree(value: string) {
    const filterValue = value.toLowerCase();

    const filterTree = (node: TreeNode): TreeNode | null => {
      const children: any = node.children
        ? node.children.map(filterTree).filter((child) => child !== null)!
        : [];

      if (node.name.toLowerCase().includes(filterValue) || children.length) {
        return { ...node, children };
      }
      return null;
    };

    this.filteredTreeData = this.originalTreeData
      .map(filterTree)
      .filter((node) => node !== null) as TreeNode[];

    // Update dataSource and tree control with new filtered data
    this.dataSource.data = this.filteredTreeData;
    this.treeControl.expandAll();
  }
}